import React, { useState, useEffect, useRef, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import useTotalTransactions from "../../hooks/billing/useTotalTransactions";
import { useTransmissionsData } from "../../hooks/patients";
import DateRangeFilter from "../../components/DateRangeFilter/DateRangeFilter";
import TransmissionsChartSkeleton from "./TransmissionsChartSkeleton";
import styles from "./RTMDashboard.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TransmissionsChart: React.FC<{ className?: string }> = ({ className }) => {
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);
  const filterRef = useRef<HTMLDivElement>(null);

  const {
    transactions,
    loading: transactionsLoading,
    getTotalTransactions,
  } = useTotalTransactions();
  const { transmissionsData, getTransmissionsData, transmissionsLoading } = useTransmissionsData();

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        try {
          await Promise.all([getTotalTransactions(), getTransmissionsData()]);
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    };
    fetchData();
  }, [getTotalTransactions, getTransmissionsData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsFilterVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const monthSequence = [
    'Jan', 'Jan-Feb', 'Feb', 'Feb-Mar', 'Mar', 'Mar-Apr',
    'Apr', 'Apr-May', 'May', 'May-Jun', 'Jun', 'Jun-Jul',
    'Jul', 'Jul-Aug', 'Aug', 'Aug-Sep', 'Sep', 'Sep-Oct',
    'Oct', 'Oct-Nov', 'Nov', 'Nov-Dec', 'Dec', 'Dec-Jan'
  ];

  const processedTransactions = useMemo(() => {
    if (!transactions) return [];
    return [...transactions].sort((a, b) => {
      const aName = (a._id || '').replace(/^BLC-/, '');
      const bName = (b._id || '').replace(/^BLC-/, '');
      const aIndex = monthSequence.indexOf(aName);
      const bIndex = monthSequence.indexOf(bName);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return aName.localeCompare(bName);
    });
  }, [transactions]);

  const handleApplyFilter = async (dates: [Dayjs | null, Dayjs | null]) => {
    setDateRange(dates);
    const [startDate, endDate] = dates;
    try {
      if (startDate && endDate) {
        const fromDate = startDate.format("M/D/YYYY");
        const toDate = endDate.format("M/D/YYYY");
        await Promise.all([
          getTotalTransactions(fromDate, toDate),
          getTransmissionsData(),
        ]);
      } else {
        await Promise.all([
          getTotalTransactions(undefined, undefined),
          getTransmissionsData(),
        ]);
      }
    } catch (error) {
      console.error("Error applying date filter:", error);
    }
  };

  const totalRequired = transmissionsData?.totalRequired || 0;
  const totalCollected = transmissionsData?.totalCollected || 0;
  const totalNeeded = transmissionsData?.totalNeeded || 0;

  const progressData = [
    {
      label: "Collected",
      value: `${totalCollected}/${totalNeeded}`,
      percent: 100,
      fillPercent: totalNeeded > 0 ? (totalCollected / totalNeeded) * 100 : 0,
      color: "#0170AE",
    },
    {
      label: "Needed",
      value: totalRequired,
      percent: 100,
      fillPercent: 100,
      color: "#0F407A",
    },
  ];

  const chartLabels = processedTransactions.map(
    (t: any) => t?._id?.replace("BLC-", "") || ""
  );

  const chartValues = processedTransactions.map((t: any) => t.totalTransaction || 0);

  // ✅ Calculate dynamic max Y value
  const maxTransactionValue = useMemo(() => {
    const max = Math.max(...chartValues, 0);
    return Math.ceil(max + 10); // add padding
  }, [chartValues]);

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Transactions",
        data: chartValues,
        backgroundColor: (context: any) => {
          const value = context.raw as number;
          return value === 0 ? "#CCCCCC" : "#0170AE";
        },
        borderRadius: 4,
        barThickness: 40,
        categoryPercentage: 0.8,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: "easeInOutQuart",
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            return `Transactions: ${context.raw}`;
          },
        },
        displayColors: false,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleFont: {
          size: 12,
        },
        bodyFont: {
          size: 14,
          weight: "bold",
        },
        padding: 10,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      y: {
        beginAtZero: true,
        max: maxTransactionValue, // ✅ dynamic max here
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          stepSize: 5,
          padding: 10,
          font: {
            size: 11,
          },
        },
      },
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10,
      },
    },
  };

  const chartHeaderExtra = (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span className={styles.dateRangeText}>
        {dateRange[0] && dateRange[1]
          ? `${dateRange[0].format("MMM D")} - ${dateRange[1].format(
              "MMM D, YYYY"
            )}`
          : "Start date - End date"}
      </span>
      <div style={{ position: "relative" }} ref={filterRef}>
        <button
          onClick={() => setIsFilterVisible(!isFilterVisible)}
          className={styles.filterButton}
        >
          <svg width="16" height="16" fill="#0F407A">
            <use href={`/assets/sprite.svg#icon-filter-icon`} />
          </svg>
          <span className={styles.filterButtonText}>Filters</span>
        </button>
        {isFilterVisible && (
          <div className={styles.filterPopover}>
            <div className={styles.popoverHeader}>
              <div className={styles.popoverTitle}>Filter By</div>
              <button
                onClick={() => setIsFilterVisible(false)}
                className={styles.closeButton}
              >
                X
              </button>
            </div>
            <DateRangeFilter
              onClose={() => setIsFilterVisible(false)}
              onApply={(dates: [Dayjs | null, Dayjs | null]) => {
                handleApplyFilter(dates);
                setIsFilterVisible(false);
              }}
              initialDates={dateRange}
            />
          </div>
        )}
      </div>
    </div>
  );

  if (transactionsLoading || transmissionsLoading) {
    return <TransmissionsChartSkeleton />;
  }

  return (
    <div
      className={`${styles.chartCard}`}
      style={{ height: "auto", display: "flex", flexDirection: "column" }}
    >
      <div className={styles.chartHeader}>
        <h3 style={{ margin: 0, fontWeight: 500, fontSize: '16px' }}>Monthly Transmissions</h3>
        {chartHeaderExtra}
      </div>
      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ marginBottom: "1rem" }}>
          {progressData.map((item, index) => (
            <div key={index} style={{ marginBottom: "1rem" }}>
              <div className={styles.progressLabel}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
              <div
                style={{
                  backgroundColor: '#E5E7EB',
                  borderRadius: '4px',
                  height: '8px',
                  width: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: `${item.fillPercent}%`,
                    backgroundColor: item.color,
                    borderRadius: '4px'
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <div style={{ height: "180px", position: "relative" }}>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export default TransmissionsChart;
